import React, { useState } from "react";
import { DELETE } from "../constant/apiMethods";
import { MdDeleteForever } from "react-icons/md";
import Labels from "../services/apiLabels";
import { AiOutlineEdit, AiFillCamera } from "react-icons/ai";
import carimg from "../assets/images.jpg";
import config from "../config";
import { ImHammer2 } from "react-icons/im";
import { resolveVehicleImageUrl } from "../utils/sanitizeImageUrl";
import { getBuyNowDisplayPrice } from "../utils/listingPrice";
import ConfirmDialog from "./ConfirmDialog";

export const API_ENDPOINT = (label) => {
  return `${config.baseUrl}${label}`;
};

function formatDate(dt) {
  if (dt == null || dt === "") return "-";
  const datt = new Date(dt);
  if (Number.isNaN(datt.getTime())) return String(dt);
  return `${datt.toLocaleDateString()} ${datt.toLocaleTimeString()}`;
}

function formatMoney(value) {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `€${num.toLocaleString()}`;
}

function pick(item, keys, fallback = "-") {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== "") return value;
  }
  return fallback;
}

function formatTimeLeft(value) {
  if (value == null || value === "") return "-";
  // ISO / date-like values
  if (
    typeof value === "string" &&
    (/^\d{4}-\d{2}-\d{2}/.test(value) || value.includes("T"))
  ) {
    return formatDate(value);
  }
  return String(value);
}

/**
 * Explicit column definitions so cells always match headers.
 * Field names follow API camelCase used by lost/won payloads.
 */
export const BID_COLUMNS = {
  active: [
    {
      label: "Reg",
      keys: ["registration", "reg", "registration_number"],
      type: "text",
    },
    { label: "Vehicle", keys: ["vehicle", "product_name", "name"], type: "text" },
    {
      label: "Bid On",
      keys: ["bidOffer", "bid_on", "bid_date", "created_at"],
      type: "date",
    },
    {
      label: "Time Left",
      keys: ["timeLeft", "time_left", "remaining", "endDate", "end_date"],
      type: "timeleft",
    },
    {
      label: "Offer",
      keys: ["myBidOffer", "offer", "bid_amount", "my_bid"],
      type: "money",
    },
    {
      label: "Current Bid",
      keys: ["currentBid", "current_bid", "cur_price", "current_price"],
      type: "money",
    },
    { label: "Action", keys: ["product_id", "productId", "id", "p_id"], type: "action" },
  ],
  upcoming: [
    {
      label: "Reg",
      keys: ["registration", "reg", "registration_number"],
      type: "text",
    },
    { label: "Vehicle", keys: ["vehicle", "product_name", "name"], type: "text" },
    {
      label: "Start In",
      keys: ["startIn", "start_in", "startDate", "start_date"],
      type: "date",
    },
    {
      label: "Amount",
      keys: ["amount", "myBidOffer", "opening_price", "openingPrice", "price"],
      type: "money",
    },
    { label: "Status", keys: ["status", "auction_status"], type: "text" },
    { label: "Action", keys: ["product_id", "productId", "id", "p_id"], type: "action" },
  ],
  lost: [
    {
      label: "Reg",
      keys: ["registration", "reg", "registration_number"],
      type: "text",
    },
    { label: "Vehicle", keys: ["vehicle", "product_name", "name"], type: "text" },
    {
      label: "Bid On",
      keys: ["bidOffer", "bid_on", "bid_date", "created_at"],
      type: "date",
    },
    {
      label: "Offer",
      keys: ["myBidOffer", "offer", "bid_amount", "my_bid"],
      type: "money",
    },
    {
      label: "Winning Bid",
      keys: ["winningBid", "winning_bid", "final_bid"],
      type: "money",
    },
    { label: "Action", keys: ["product_id", "productId", "id", "p_id"], type: "action" },
  ],
  won: [
    {
      label: "Reg",
      keys: ["registration", "reg", "registration_number"],
      type: "text",
    },
    { label: "Vehicle", keys: ["vehicle", "product_name", "name"], type: "text" },
    {
      label: "Bought On",
      keys: ["boughtOn", "bought_on", "purchase_date"],
      type: "date",
    },
    {
      label: "Price",
      keys: ["price", "myBidOffer", "amount"],
      type: "money",
    },
    { label: "Status", keys: ["status", "auction_status"], type: "text" },
    { label: "Action", keys: ["product_id", "productId", "id", "p_id"], type: "action" },
  ],
};

function renderConfiguredCell(column, item, onViewBid) {
  const raw = pick(item, column.keys, null);
  switch (column.type) {
    case "date":
      return formatDate(raw);
    case "money":
      return formatMoney(raw);
    case "timeleft":
      return formatTimeLeft(raw);
    case "action": {
      if (raw == null || raw === "-") return "-";
      return (
        <button
          type="button"
          className="dd-btn dd-btn--ghost"
          onClick={() => onViewBid?.(item)}
        >
          View
        </button>
      );
    }
    case "text":
    default:
      return raw == null ? "-" : String(raw);
  }
}

const Table = ({
  myVehiclesList,
  getAllCars,
  setisEdit,
  setSelectedTabIdx,
  biddingAuction,
  columns,
  bidColumns,
  onViewBid,
}) => {
  const resolvedBidColumns =
    bidColumns ||
    (Array.isArray(columns) && typeof columns[0] === "object"
      ? columns
      : null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const edit = (item) => {
    setisEdit(item);
    setSelectedTabIdx(1);
  };

  const requestDelete = (item) => {
    setDeleteTarget(item);
  };

  const confirmDelete = () => {
    if (!deleteTarget?.id || deleting) return;
    setDeleting(true);
    fetchData({
      label: `${Labels.delvechiel}${deleteTarget.id}/`,
      method: DELETE,
      hideOverlayLoader: true,
    });
  };

  function fetchData(payload) {
    const { label, data, method } = payload;
    fetch(API_ENDPOINT(label), {
      method,
      body: data,
    })
      .then(() => {
        if (getAllCars) getAllCars();
      })
      .finally(() => {
        setDeleting(false);
        setDeleteTarget(null);
      });
  }

  const emptyRow = (colSpan) => (
    <tr>
      <td colSpan={colSpan || 6}>
        <div className="dd-empty">
          <strong>No records yet</strong>
          <p>Nothing to show in this list right now.</p>
        </div>
      </td>
    </tr>
  );

  const headerLabels = resolvedBidColumns
    ? resolvedBidColumns.map((c) => c.label)
    : columns;

  return (
    <div className="dd-table-wrap">
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete vehicle?"
        message={
          <p>
            You are about to permanently remove{" "}
            <strong>{deleteTarget?.name || "this vehicle"}</strong>
            {deleteTarget?.brand_name
              ? ` (${deleteTarget.brand_name}${
                  deleteTarget.model_name ? ` ${deleteTarget.model_name}` : ""
                })`
              : ""}
            . This cannot be undone.
          </p>
        }
        confirmLabel="Delete"
        confirming={deleting}
        confirmingLabel="Deleting…"
        onCancel={() => (!deleting ? setDeleteTarget(null) : null)}
        onConfirm={confirmDelete}
      />
      <table className="dd-table table-class">
        <thead className="active-color">
          <tr>
            {headerLabels?.map((items) => (
              <th key={items} scope="col" className="header-class">
                {items}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {biddingAuction &&
            (biddingAuction?.length >= 1
              ? biddingAuction.map((item, index) => (
                  <tr key={item.product_id || item.id || index}>
                    {resolvedBidColumns
                      ? resolvedBidColumns.map((column) => (
                          <td key={column.label} className="body-class">
                            {renderConfiguredCell(column, item, onViewBid)}
                          </td>
                        ))
                      : null}
                  </tr>
                ))
              : emptyRow(headerLabels?.length))}

          {myVehiclesList &&
            (myVehiclesList?.length >= 1
              ? [...myVehiclesList].reverse().map((item) => (
                  <tr key={item.id}>
                    <td className="body-class">{item.name}</td>
                    <td className="body-class">
                      {`${item.brand_name}-${item.model_name}-${item.fuel_type}`}
                    </td>
                    <td className="body-class">
                      {getBuyNowDisplayPrice(item) ?? "-"}
                    </td>
                    <td className="body-class">
                      <img
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: "cover",
                          borderRadius: 8,
                        }}
                        src={resolveVehicleImageUrl(item, carimg)}
                        alt=""
                      />
                    </td>
                    <td className="body-class">
                      <div className="dd-btn-row" style={{ marginTop: 0 }}>
                        {!item.allow_delete && (
                          <button
                            type="button"
                            className="dd-btn dd-btn--ghost"
                            onClick={() => requestDelete(item)}
                            aria-label="Delete"
                          >
                            <MdDeleteForever size={18} />
                          </button>
                        )}
                        {!item.hideEdit && (
                          <button
                            type="button"
                            className="dd-btn dd-btn--ghost"
                            onClick={() => edit(item)}
                            aria-label="Edit"
                          >
                            <AiOutlineEdit size={18} />
                          </button>
                        )}
                        {!item.hideCamera && (
                          <button
                            type="button"
                            className="dd-btn dd-btn--ghost"
                            onClick={() => {
                              window.open(
                                `/inspection/dealerinspection?id=${encodeURIComponent(
                                  item.inspect_id
                                )}`,
                                "_blank",
                                "noopener,noreferrer"
                              );
                            }}
                            aria-label="Inspection"
                          >
                            <AiFillCamera size={18} />
                          </button>
                        )}
                        <span className="dd-btn dd-btn--ghost" aria-hidden>
                          <ImHammer2 size={16} />
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              : emptyRow(headerLabels?.length || 5))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
